import React, { useState } from "react";
import { Edit2, Plus, X } from "react-feather";
import { useTheme } from "../theme/ThemeContext";
import { isInPeriod } from "../utils/period";
import { fixedExpenseTest } from "../utils/bills";
import { isSpending } from "../utils/expenseTypes";
import { Card, Button, Field, Sheet, useInputStyle, useMoney, formatDate, todayIso } from "../mobile/ui";

// Budgets come from the budgets table (see server/db.js):
//   monthly:  how much random (one-off) spending is planned for a month. Fixed
//             payments - EMIs, SIPs, rent, subscriptions - are already planned on
//             Bills & Payments, so they're shown next to it rather than budgeted again.
//   category: part of the monthly budget for one type of random spending ("Groceries").
//             When there are any, the monthly budget is their sum.
//   one_time: a goal like a trip or a gadget, filled by what's been saved toward it.

// Each bar stays green below 98% of its budget, turns yellow from there up to the full
// amount, and red once it's gone over.
export const BUDGET_WARN = 0.98;
export const budgetColor = (used, tokens) => (used > 1 ? tokens.negative : used >= BUDGET_WARN ? tokens.pending : tokens.positive);

export const monthlyBudgetOf = (budgets = []) => budgets.find((b) => b.kind === "monthly") || null;
export const categoryBudgetsOf = (budgets = []) => budgets.filter((b) => b.kind === "category" && b.amount > 0);
export const oneTimeBudgetsOf = (budgets = []) => budgets.filter((b) => b.kind === "one_time");

// Types random spending can be budgeted by. The first three are offered to start with.
const DEFAULT_CATEGORIES = ["Groceries", "Food & Dining", "Shopping"];
const CATEGORY_TYPES = [
  ...DEFAULT_CATEGORIES,
  "Transport", "Entertainment", "Travel", "Health", "Personal Care", "Education", "Gifts & Donations", "Family", "Purchase", "Other",
];

// This month's spending split into fixed and random, against the monthly budget and
// each category's. Every expense dated this month counts, paid or not; money lent,
// loans paid back and card bill payments don't.
export const monthBudgetStatus = (allBills, budgets) => {
  const bills = allBills.filter(isSpending);
  const isFixed = fixedExpenseTest(bills);
  const month = bills.filter((b) => isInPeriod(b.dueDate, "Month"));
  const fixed = month.filter(isFixed).sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1));
  const fixedTotal = fixed.reduce((s, b) => s + b.amount, 0);
  const random = month.filter((b) => !isFixed(b));
  const spent = random.reduce((s, b) => s + b.amount, 0);
  const categories = categoryBudgetsOf(budgets).map((c) => {
    const catSpent = random.filter((b) => b.type === c.name).reduce((s, b) => s + b.amount, 0);
    return { id: c.id, name: c.name, limit: c.amount, spent: catSpent, used: catSpent / c.amount };
  });
  const names = new Set(categories.map((c) => c.name));
  const otherSpent = random.filter((b) => !names.has(b.type)).reduce((s, b) => s + b.amount, 0);
  // With categories, the overall budget is their sum; otherwise the amount set for it.
  const monthly = monthlyBudgetOf(budgets);
  const limit = categories.length ? categories.reduce((s, c) => s + c.limit, 0) : monthly && monthly.amount > 0 ? monthly.amount : null;
  return { fixed, fixedTotal, spent, limit, used: limit ? spent / limit : 0, categories, otherSpent };
};

const monthName = () => new Date().toLocaleDateString("en-US", { month: "long" });

// One budget line: name, spent / limit, a bar, and what's left.
const BudgetLine = ({ name, spent, limit, strong }) => {
  const { tokens, fonts } = useTheme();
  const money = useMoney();
  const used = limit ? spent / limit : 0;
  const color = budgetColor(used, tokens);
  const left = limit - spent;
  return (
    <div style={{ padding: "7px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: strong ? 13.5 : 13, color: tokens.textPrimary }}>
        <span style={{ fontWeight: strong ? 700 : 600 }}>{name}</span>
        <span style={{ fontFamily: fonts.mono, fontWeight: 600, whiteSpace: "nowrap" }}>
          {money(spent)} <span style={{ color: tokens.textMuted, fontWeight: 400 }}>/ {money(limit)}</span>
        </span>
      </div>
      <div style={{ height: strong ? 12 : 7, borderRadius: 6, marginTop: 6, overflow: "hidden", backgroundColor: tokens.neutralSoft }}>
        <div style={{ width: `${Math.min(100, used * 100)}%`, height: "100%", backgroundColor: color }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 11.5, color: used >= BUDGET_WARN ? color : tokens.textMuted }}>
        <span>{Math.round(used * 100)}% spent</span>
        <span>{left >= 0 ? `${money(left)} left` : `Over by ${money(-left)}`}</span>
      </div>
    </div>
  );
};

// Setting the budget: an amount per category (Groceries, Food & Dining and Shopping to
// start with; more can be added, any removed), and the overall budget - their sum, or
// typed in when no category has an amount.
const BudgetEditor = ({ budgets, api, onDone }) => {
  const { tokens, fonts } = useTheme();
  const money = useMoney();
  const input = useInputStyle();
  const existing = budgets.filter((b) => b.kind === "category");
  const monthly = monthlyBudgetOf(budgets);
  const [rows, setRows] = useState(() => {
    const set = existing.filter((c) => c.amount > 0).map((c) => ({ name: c.name, amount: String(c.amount) }));
    return set.length ? set : DEFAULT_CATEGORIES.map((name) => ({ name, amount: "" }));
  });
  const [overall, setOverall] = useState(monthly && monthly.amount > 0 && !existing.some((c) => c.amount > 0) ? String(monthly.amount) : "");
  const [adding, setAdding] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const amounts = rows.map((r) => (r.amount.trim() === "" ? 0 : parseFloat(r.amount)));
  const sum = amounts.reduce((s, n) => s + (Number.isNaN(n) ? 0 : n), 0);
  const byCategory = sum > 0;
  const unused = CATEGORY_TYPES.filter((t) => !rows.some((r) => r.name === t));

  const setAmount = (i, value) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, amount: value } : r)));
  const addCategory = (name) => {
    if (!name) return;
    setRows((rs) => [...rs, { name, amount: "" }]);
    setAdding("");
  };

  const save = async () => {
    if (amounts.some((n) => Number.isNaN(n) || n < 0)) return setError("Enter a valid amount for each category, or leave it empty.");
    const total = byCategory ? sum : overall.trim() === "" ? 0 : parseFloat(overall);
    if (Number.isNaN(total) || total < 0) return setError("Enter a valid overall budget.");
    setSaving(true);
    setError("");
    try {
      // Categories: set the ones with an amount, remove the rest.
      const keep = new Set();
      for (let i = 0; i < rows.length; i += 1) {
        if (amounts[i] > 0) {
          keep.add(rows[i].name);
          await api.create("budgets", { kind: "category", name: rows[i].name, amount: amounts[i] });
        }
      }
      for (const c of existing) if (!keep.has(c.name)) await api.remove("budgets", c.id);
      // The overall budget: the categories' sum, or the amount typed in.
      if (total > 0) await api.create("budgets", { kind: "monthly", amount: total });
      else if (monthly) await api.remove("budgets", monthly.id);
      onDone();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 12, color: tokens.textMuted, marginBottom: 8 }}>How much for each kind of one-off spending this month. Leave one empty to not budget it.</div>
      {rows.map((r, i) => (
        <div key={r.name} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 14, color: tokens.textPrimary }}>{r.name}</span>
          <input style={{ ...input, width: 130 }} type="number" inputMode="decimal" placeholder="₹ amount" value={r.amount} onChange={(e) => setAmount(i, e.target.value)} aria-label={`${r.name} budget`} />
          <button type="button" aria-label={`Remove ${r.name}`} onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} style={{ display: "flex", padding: 6, border: "none", borderRadius: 8, cursor: "pointer", background: "none", color: tokens.textMuted }}>
            <X size={16} />
          </button>
        </div>
      ))}
      {unused.length > 0 && (
        <select style={{ ...input, marginBottom: 12 }} value={adding} onChange={(e) => addCategory(e.target.value)} aria-label="Add a category">
          <option value="">+ Add a category</option>
          {unused.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      )}
      <div style={{ padding: 12, borderRadius: 12, backgroundColor: tokens.neutralSoft, marginBottom: 12 }}>
        {byCategory ? (
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: tokens.textPrimary }}>
            <span><strong>Overall budget</strong> (sum of categories)</span>
            <span style={{ fontFamily: fonts.mono, fontWeight: 700 }}>{money(sum)}</span>
          </div>
        ) : (
          <Field label="Overall budget (₹) - used when no category has an amount">
            <input style={input} type="number" inputMode="decimal" value={overall} onChange={(e) => setOverall(e.target.value)} placeholder="e.g. 15000" />
          </Field>
        )}
      </div>
      {error && <div role="alert" style={{ marginBottom: 8, fontSize: 13, color: tokens.negative }}>{error}</div>}
      <div style={{ display: "flex", gap: 10 }}>
        <Button onClick={save} disabled={saving} style={{ flex: 1 }}>{saving ? "Saving…" : "Save budget"}</Button>
        <Button kind="ghost" onClick={onDone} style={{ flex: 1 }}>Cancel</Button>
      </div>
    </div>
  );
};

// The random-spending budget: the overall bar, then one per category. (Fixed payments
// aren't part of it; they're on the Fixed tab.) `api` (create / update / remove for "budgets")
// shows the edit button; without it the card is read-only.
export const MonthlyBudgetCard = ({ bills, budgets, api }) => {
  const { tokens, fonts } = useTheme();
  const money = useMoney();
  const { spent, limit, categories, otherSpent } = monthBudgetStatus(bills, budgets);
  const [editing, setEditing] = useState(false);

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: tokens.textPrimary }}>{monthName()} budget</span>
        {api && !editing && (
          <button onClick={() => setEditing(true)} style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: tokens.accent }}>
            <Edit2 size={13} /> {limit ? "Edit" : "Set budget"}
          </button>
        )}
      </div>

      {/* Random spending only: overall and by category. Fixed payments are on the Fixed tab. */}
      <div style={{ marginTop: 10 }}>
        {editing ? (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: tokens.textPrimary }}>Random spending</div>
            <BudgetEditor budgets={budgets} api={api} onDone={() => setEditing(false)} />
          </>
        ) : limit ? (
          <>
            <BudgetLine name={categories.length ? "Random spending (all categories)" : "Random spending"} spent={spent} limit={limit} strong />
            {categories.length > 0 && (
              <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px solid ${tokens.surfaceBorder}` }}>
                {categories.map((c) => <BudgetLine key={c.name} name={c.name} spent={c.spent} limit={c.limit} />)}
                {otherSpent > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", fontSize: 13, color: tokens.textMuted }}>
                    <span>Everything else (no budget of its own)</span>
                    <span style={{ fontFamily: fonts.mono, fontWeight: 600 }}>{money(otherSpent)}</span>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: tokens.textPrimary }}>
              <span style={{ fontWeight: 700 }}>Random spending</span>
              <span style={{ fontFamily: fonts.mono, fontWeight: 600 }}>{money(spent)}</span>
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: tokens.textMuted }}>
              Set a budget for groceries, food & dining, shopping or anything else you spend on now and then, to see how much is left. You'll get a phone alert at 98%.
            </div>
          </>
        )}
      </div>

    </Card>
  );
};

// ---- one-time budgets (trips, gadgets) -------------------------------------------------

const daysLeft = (iso) => Math.round((new Date(`${iso}T00:00:00`) - new Date(`${todayIso()}T00:00:00`)) / 86400000);

// "by Dec 20 · 81 days left · save ₹4,000 a month", or what's still missing when undated.
const goalNote = (b, money) => {
  const missing = Math.max(0, b.amount - b.saved);
  if (missing === 0) return "Fully saved";
  if (!b.targetDate) return `${money(missing)} to go`;
  const days = daysLeft(b.targetDate);
  if (days < 0) return `Was needed by ${formatDate(b.targetDate)} · ${money(missing)} short`;
  const months = Math.max(1, Math.ceil(days / 30));
  return `By ${formatDate(b.targetDate)} · ${days} day${days === 1 ? "" : "s"} left · save ${money(Math.ceil(missing / months))} a month`;
};

// One bar per one-time budget, filling up as money is saved toward it. `onOpen(budget)`
// makes each row tappable, `onAdd` shows an add button.
export const PlannedBudgets = ({ budgets, onOpen, onAdd, title = "Planned budgets" }) => {
  const { tokens, fonts } = useTheme();
  const money = useMoney();
  const goals = oneTimeBudgetsOf(budgets);
  const saved = goals.reduce((s, b) => s + Math.min(b.saved, b.amount), 0);
  const needed = goals.reduce((s, b) => s + b.amount, 0);

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: goals.length ? 4 : 0 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: tokens.textPrimary }}>{title}</span>
        {onAdd && (
          <button onClick={onAdd} style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: tokens.accent }}>
            <Plus size={14} /> Add
          </button>
        )}
      </div>
      {goals.length === 0 && (
        <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 6 }}>
          Planning a trip or saving for a new phone? Add it here with how much it needs, and fill the bar as you save.
        </div>
      )}
      {goals.map((b) => {
        const pct = b.amount > 0 ? Math.min(1, b.saved / b.amount) : 1;
        const done = pct >= 1;
        const body = (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13, color: tokens.textPrimary }}>
              <span style={{ fontWeight: 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.name}</span>
              <span style={{ fontFamily: fonts.mono, fontWeight: 600, whiteSpace: "nowrap" }}>
                {money(b.saved)} <span style={{ color: tokens.textMuted, fontWeight: 400 }}>/ {money(b.amount)}</span>
              </span>
            </div>
            <div style={{ height: 8, borderRadius: 4, marginTop: 6, backgroundColor: tokens.neutralSoft, overflow: "hidden" }}>
              <div style={{ width: `${pct * 100}%`, height: "100%", backgroundColor: done ? tokens.positive : tokens.accent }} />
            </div>
            <div style={{ marginTop: 4, fontSize: 11, color: done ? tokens.positive : tokens.textMuted }}>
              {Math.round(pct * 100)}% saved · {goalNote(b, money)}
            </div>
          </>
        );
        return onOpen ? (
          <button key={b.id} type="button" onClick={() => onOpen(b)} style={{ display: "block", width: "100%", padding: "9px 0", background: "none", border: "none", textAlign: "left", cursor: "pointer" }}>
            {body}
          </button>
        ) : (
          <div key={b.id} style={{ padding: "9px 0" }}>{body}</div>
        );
      })}
      {goals.length > 1 && (
        <div style={{ marginTop: 6, paddingTop: 10, borderTop: `1px solid ${tokens.surfaceBorder}`, display: "flex", justifyContent: "space-between", fontSize: 12, color: tokens.textMuted }}>
          <span>Saved across all</span>
          <span style={{ fontFamily: fonts.mono, fontWeight: 700, color: tokens.textPrimary }}>
            {money(saved)} / {money(needed)}
          </span>
        </div>
      )}
    </Card>
  );
};

// Add or edit a one-time budget, and add money saved toward it. `data` is anything
// with create / update / remove for the "budgets" collection (see useMobileData).
export const PlannedBudgetSheet = ({ budget, data, onClose }) => {
  const { tokens } = useTheme();
  const money = useMoney();
  const input = useInputStyle();
  const editing = !!budget;
  const [form, setForm] = useState({
    name: budget ? budget.name : "",
    amount: budget ? String(budget.amount) : "",
    saved: budget ? String(budget.saved) : "",
    targetDate: budget && budget.targetDate ? budget.targetDate : "",
    description: budget ? budget.description || "" : "",
  });
  const [adding, setAdding] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const run = async (fn) => {
    setSaving(true);
    setError("");
    try {
      await fn();
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    const saved = form.saved.trim() === "" ? 0 : parseFloat(form.saved);
    if (!form.name.trim()) return setError("What is it for? For example \"Goa trip\" or \"New phone\".");
    if (Number.isNaN(amount) || amount <= 0) return setError("Enter how much it needs.");
    if (Number.isNaN(saved) || saved < 0) return setError("Enter a valid amount saved, or leave it empty.");
    const body = { name: form.name.trim(), amount, saved, targetDate: form.targetDate || null, description: form.description.trim() };
    return run(() => (editing ? data.update("budgets", budget.id, body) : data.create("budgets", { kind: "one_time", ...body })));
  };

  const addSavings = () => {
    const n = parseFloat(adding);
    if (Number.isNaN(n) || n <= 0) return setError("Enter how much you've put aside.");
    return run(() => data.update("budgets", budget.id, { saved: budget.saved + n }));
  };

  return (
    <Sheet title={editing ? budget.name : "Add a planned budget"} onClose={onClose}>
      {editing && (
        <div style={{ marginBottom: 16, padding: 12, borderRadius: 12, backgroundColor: tokens.neutralSoft }}>
          <div style={{ fontSize: 13, color: tokens.textPrimary, marginBottom: 8 }}>
            Saved <strong>{money(budget.saved)}</strong> of {money(budget.amount)}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <input style={{ ...input, flex: 1 }} type="number" inputMode="decimal" value={adding} onChange={(e) => setAdding(e.target.value)} placeholder="Amount put aside" aria-label="Amount put aside" />
            <Button type="button" disabled={saving} onClick={addSavings}>Add</Button>
          </div>
        </div>
      )}
      <form onSubmit={submit}>
        <Field label="What is it for?">
          <input style={input} autoFocus={!editing} value={form.name} onChange={set("name")} placeholder="e.g. Goa trip, New phone" />
        </Field>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Field label="It needs (₹)"><input style={input} type="number" inputMode="decimal" value={form.amount} onChange={set("amount")} placeholder="0" /></Field>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Field label="Saved so far (₹)"><input style={input} type="number" inputMode="decimal" value={form.saved} onChange={set("saved")} placeholder="0" /></Field>
          </div>
        </div>
        <Field label="Needed by (optional)">
          <input style={input} type="date" value={form.targetDate} onChange={set("targetDate")} />
        </Field>
        <Field label="Description (optional)">
          <input style={input} value={form.description} onChange={set("description")} placeholder="A note for later" />
        </Field>
        {error && <div role="alert" style={{ marginBottom: 10, fontSize: 13, color: tokens.negative }}>{error}</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
          {editing && !confirmDelete && <Button type="button" kind="danger" onClick={() => setConfirmDelete(true)}>Delete</Button>}
          {editing && confirmDelete && (
            <Button type="button" kind="danger" disabled={saving} onClick={() => run(() => data.remove("budgets", budget.id))}>Tap again to delete</Button>
          )}
        </div>
      </form>
    </Sheet>
  );
};
